import { CommandBar, DetailsList, Selection, Icon, Panel, PanelType, SelectionMode, TooltipHost, MessageBar, MessageBarType, Spinner, IconButton, Stack, PrimaryButton } from "office-ui-fabric-react";
import { useEffect, useState } from "react";
import { cancelBooking, searchDeskBookings } from "../../api/bookings";
import { IBooking, IMyBooking, IBookingSearchParams } from "../../types/Booking.types";
import { CancelReasonDialog } from "../settings/menus/CancelReasonDialog";

export interface ICancelBookingsPanelProps {
    deskId: number | undefined;
    onDismiss: (deskId: number | undefined) => void;
}

export const CancelBookingsPanel = (props: ICancelBookingsPanelProps) => {
    const { onDismiss } = props;

    const [ cancelling, setCancelling ] = useState(false);
    const [ bookings, setBookings ] = useState<IMyBooking[]>();
    const [ bookingSelected, setBookingSelected ] = useState<boolean|undefined>();
    const [ selectedBookings, setSelectedBookings ] = useState<IMyBooking[]>([]);
    const [ showDialog, setShowDialog ] = useState(false);

    const onSelectionChanged = () => {
        const selectedItems = selection.getSelection() as IMyBooking[];
        if (selectedItems !== undefined && selectedItems.length > 0) {
            setBookingSelected(true);
            setSelectedBookings(selectedItems);
        } else {
            setBookingSelected(false);
            setSelectedBookings([]);
        }
    };
    
    const [ selection ] = useState(new Selection({ onSelectionChanged }));

    const _load = async () => {
        const response = await searchDeskBookings({
            FromDate: new Date().toISOString(),
            DeskId: props.deskId
        });
        if (response.ok) {
            const bookings = await response.json() as IMyBooking[];
            setBookings(bookings);
        }
    }    

    const _cancelBulkBookings = async (bookings: IMyBooking[], cancelReason: string | undefined) => {
        setCancelling(true);
        let cancelSuccess = 0;
        for (let index = 0; index < bookings.length; index++) {
            const booking = bookings[index];
            booking.CancelledByAdmin = true;
            booking.CancellationReason = cancelReason;
            const response = await cancelBooking(booking);
            if (response.ok) {
                cancelSuccess++;
            }
        }

        if (bookings.length == cancelSuccess) {
            alert('All selected bookings cancelled.');
            setCancelling(false);
            setSelectedBookings([]);
            _load();
        } else {        
            if (cancelSuccess == 0) {
                alert('Unable to cancel bookings');
                setCancelling(false);
                setSelectedBookings([]);
            } else {
                alert(cancelSuccess + ' out of ' + bookings.length + ' bookings cancelled. There were issues cancelling the remaining bookings')
                setCancelling(false);
                setSelectedBookings([]);
                _load();
            }
        }
    }

    useEffect(() => {
        _load();
    }, []);

    return (
        <Panel type={PanelType.custom} customWidth="900px" isOpen={true} headerText="Desk Bookings" onDismiss={() => onDismiss(props.deskId)}>
            { window.innerWidth < 640 && (
                <MessageBar messageBarType={MessageBarType.info}>You can scroll the table below with the mouse or your finger</MessageBar>
            )}
            <CommandBar
                items={[
                    {
                        disabled: !bookingSelected || cancelling,
                        key: "cancel",
                        text: window.innerWidth < 640 ? "Cancel" : "Cancel Booking",
                        onRender: cancelling ? () => <Spinner label="Cancelling..." /> : undefined,
                        onClick: () => {
                            const selectedItems = selection.getSelection() as IMyBooking[];
                            if (selectedItems) {
                                setSelectedBookings(selectedItems);
                                setShowDialog(true);
                            }
                        }
                    }
                ]}
            />
            { bookings !== undefined && bookings.length > 0 && [
                <DetailsList
                    items={bookings}
                    // className={styles.myBookingsTable}
                    columns={[
                        {
                            key: "deskName",
                            name: "Desk",
                            fieldName: "DeskName",
                            minWidth: 80,
                            maxWidth: 100,
                            onRender: (item: IMyBooking, index, column) => {
                                // For other orgs or if sites expand may want to list location and floorplan here too 
                                let deskName = item.DeskName;
                                if (!deskName || deskName.trim().length === 0) deskName = "Desk (Id: " + item.DeskId + ")";
                                return (
                                    <div>
                                        {deskName}
                                        <div style={{fontSize: 10}}>
                                            {item.SiteName}
                                        </div>
                                    </div>
                                )
                            }
                        },
                        {
                            key: "onBehalf",
                            name: "On Behalf Of",
                            fieldName: "OnBehalfOfEmail",
                            minWidth: 80,
                            maxWidth: 100,
                            onRender: (item: IMyBooking) => {
                                return item.OnBehalfOfEmail ? item.OnBehalfOfEmail : "Self";
                            },
                        },
                        {
                            key: "start",
                            name: "Start",
                            fieldName: "Start",
                            minWidth: 80,
                            maxWidth: 150,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.Start) {
                                    const date = new Date(item.Start);
                                    let dateStr = date.toLocaleDateString();
                                    if (window.innerWidth < 768) dateStr = dateStr.substr(0, 5);
                                    return  dateStr + " " + date.toLocaleTimeString().substr(0,5);
                                }
                            }
                        },
                        {
                            key: "finish",
                            name: "Finish",
                            fieldName: "Finish",
                            minWidth: 80,
                            maxWidth: 150,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.Finish) {
                                    const date = new Date(item.Finish);
                                    let dateStr = date.toLocaleDateString();
                                    if (window.innerWidth < 768) dateStr = dateStr.substr(0, 5);
                                    return  dateStr + " " + date.toLocaleTimeString().substr(0,5);
                                }
                            }
                        },
                        {
                            key: "parking",
                            name: "Parking",
                            fieldName: "IncludeParking",
                            iconName: "ParkingSolid",
                            isIconOnly: true,
                            minWidth: 40,
                            maxWidth: 40,
                            onRender: (item: IMyBooking, index, column) => {
                                if (item.IncludeParking) {
                                    return <TooltipHost content="Parking Booked">
                                        <Icon iconName="ParkingSolid" style={{fontSize: 24}} />
                                    </TooltipHost>;
                                }
                            }
                        }
                    ]}
                    selection={selection}
                    selectionMode={SelectionMode.multiple}
                />
            ]}
            { (bookings === undefined || bookings.length === 0) &&(
                <MessageBar messageBarType={MessageBarType.info}>No bookings found for the chosen desk.</MessageBar>
            )}
            { showDialog && (
                <CancelReasonDialog 
                    onDismiss={(cancel, cancelReason) => {
                        if (cancel) _cancelBulkBookings(selectedBookings, cancelReason);
                        setShowDialog(false);
                    }}
                />
            )}                        
        </Panel> 
    );
}